export const candidateProfile = {
  name: "Wesley J Adeyinka-Skold",
  title: "Product Owner",
  tagline:
    "I own work across the software development life cycle—from backlog grooming and user stories to release communications, launch support, and product iteration. I apply a customer-centric approach in every channel: listen to user needs, understand context, advocate for clarity, and anticipate friction before it reaches production. As a Product Owner at SS&C Intralinks and founder of Ribbons and Immerse, I partner with engineering, UX, and business stakeholders to ship digital experiences customers trust.",
  location: "Inglewood, CA",
  phoneDisplay: "(323) 451-9792",
  phoneTel: "+13234519792",
};

const publicUrl = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
/** Files live in frontend/public/Videos/ */
const videoAsset = (fileName) => `${publicUrl}/Videos/${encodeURIComponent(fileName)}`;
const publicAsset = (path) => `${publicUrl}${path}`;

export const profileImage = {
  url: publicAsset("/wes-intro-photo.png"),
  alt: "Professional portrait of Wesley J Adeyinka-Skold",
};

const resumePdfFileName = "WesleyAdeyinka-SkoldResume.pdf";
const intralinks2022AwardImageFileName = "Intralinks-2022-INvaluable-Award-Wes_Adeyinka-Skold.png";

export const outroVideo = {
  url: videoAsset("Wes Outro.mp4"),
};

export const outroThumbnail = {
  url: videoAsset("Wes-outro-poster.png"),
};

export const resumePdf = {
  url: `${publicUrl}/${resumePdfFileName}`,
  fileName: resumePdfFileName,
};

export const intralinks2022Award = {
  imageUrl: `${publicUrl}/${intralinks2022AwardImageFileName}`,
  title: "2022 INvaluable Award",
  alt: "SS&C Intralinks 2022 INvaluable Award certificate presented to Wesley Adeyinka-Skold",
};

export const quickStats = [
  { label: "customer-facing product experience", value: "5+ years", id: "experience" },
  { label: "software products founded", value: "2", id: "products" },
  { label: "CSAT maintained (enterprise CSM)", value: "98%", id: "csat" },
];

export const experienceItems = [
  {
    id: "dealcentre-ai",
    role: "Product Owner – DealCentre AI",
    company: "SS&C Intralinks",
    period: "Mar 2024 – Present",
    highlight:
      "Own product strategy and backlog delivery by translating market insights into prioritized, execution-ready user stories.",
    bullets: [
      "Lead backlog grooming and write clear user stories with acceptance criteria in partnership with engineering and design.",
      "Partner with Customer Success and Sales on rollout strategy, stakeholder communications, and launch support.",
      "Use Gainsight PX for platform reporting, user analytics, and feedback loops to validate hypotheses, triage issues, and guide UX improvements.",
    ],
    tags: ["Backlog & Grooming", "User Stories & Acceptance Criteria", "Gainsight PX (Platform Reporting)"],
  },
  {
    id: "senior-csm",
    role: "Senior Customer Success Manager",
    company: "SS&C Intralinks",
    period: "Nov 2021 – Mar 2024",
    highlight:
      "Served as the voice of the customer for enterprise accounts, surfacing product gaps and informing roadmap priorities.",
    bullets: [
      "Led customer-facing support during a company-wide CRM transition (RightNow → Gainsight), including a centralized feedback hub and parity guide—recognized with an INvaluable Award.",
      "Maintained 98% CSAT across VIP enterprise accounts.",
      "Developed release communications, field training materials, and best practices to improve user outcomes.",
    ],
    tags: ["Customer-Facing Product", "Field Training & Enablement", "Stakeholder Communications"],
  },
  {
    id: "legal-assistant",
    role: "Legal Assistant (Portuguese & Spanish)",
    company: "Martin Law",
    period: "Jul 2019 – Nov 2021",
    highlight:
      "Improved access and trust for multilingual clients while supporting case process communication and issue resolution.",
    bullets: [
      "Served as primary liaison/interpreter across phone, email, and video channels.",
      "Expanded Portuguese-speaking clientele by 100%+.",
      "Scaled multilingual client support by implementing translation protocols and hiring best practices.",
    ],
    tags: ["Stakeholder Management", "Change Management", "Client Advocacy"],
  },
  {
    id: "interpreter",
    role: "Interpreter (Portuguese & Spanish)",
    company: "Self-Employed",
    period: "Apr 2016 – Jul 2019",
    highlight:
      "Enabled equitable service access for limited-English-proficiency clients in legal, healthcare, and social-service settings.",
    bullets: [
      "Delivered on-site consecutive interpretation in high-stakes interactions.",
      "Supported hundreds of family healthcare and school appointments.",
      "Adapted language and context rapidly across industries.",
    ],
    tags: ["Communication", "Client Advocacy", "Adaptability"],
  },
];

export const keyProjects = [
  {
    id: "ribbons",
    title: "Ribbons",
    company: "Founder · ribbons",
    websiteUrl: "https://www.ribbons.work",
    logoUrl: publicAsset("/logos/ribbons-logo.png"),
    logoAlt: "Ribbons logo — stylized blue letter R",
    headerAccent: "from-blue-100/80 via-blue-50/60 to-white",
    summary:
      "Work-in-progress monitoring software that helps workers visualize and manage every active initiative—reducing the cognitive load of tracking due dates and juggling projects without the overhead of a Gantt chart.",
    cards: [
      {
        type: "Challenge",
        text: "Knowledge workers carry too many concurrent initiatives in their heads—due dates, status, and capacity—creating cognitive load that slows focus and breeds dropped balls.",
      },
      {
        type: "Action",
        text: "Built a WIP-first view that tracks the status and progress of current initiatives—not a future plan. Users see how long work actually takes and how many threads they can realistically juggle at once.",
      },
      {
        type: "Result",
        text: "Helps users stay mindful of workload and duration, making capacity visible instead of guessed—so they can manage attention, not just deadlines.",
      },
    ],
    tags: ["Product Strategy", "Roadmap & Decision Making", "Customer-Centric Design"],
  },
  {
    id: "immerse",
    title: "Immerse",
    company: "Founder · immerse · iOS & Android",
    websiteUrl: "https://www.immerse.music",
    logoUrl: publicAsset("/logos/immerse-logo.png"),
    logoAlt: "Immerse logo — stylized im wordmark",
    headerAccent: "from-purple-100/80 via-purple-50/60 to-white",
    summary:
      "A web platform and companion mobile app (iOS and Android) that removes barriers to practice and rehearsal for music groups—so at showtime, performers convey the emotion and spirit of the song, not just the notes.",
    cards: [
      {
        type: "Challenge",
        text: "Song materials live across disparate apps, making personal practice fragmented. Playback tools like YouTube and Spotify lack loop, speed, and section controls—forcing singers to fight the progress bar instead of rehearsing.",
      },
      {
        type: "Action",
        text: "Centralized every song asset—lyrics, source tracks, annotations, and voice recordings—in one interface organized by setlists and folders, with one-click distribution for directors. Built native iOS and Android apps with loop, speed control, lyric-synced bookmarks, and section skip for focused repetition on any device.",
      },
      {
        type: "Result",
        text: "Members practice efficiently at home in targeted sessions; directors spend less time on parts and more on dynamics, blend, and emotion—so the group is ready to fully immerse the audience.",
      },
    ],
    tags: ["iOS & Android Apps", "Customer-Facing Product", "Platform Strategy"],
  },
];

export const skillGroups = [
  {
    id: "product",
    title: "Product Ownership",
    items: [
      "User stories & acceptance criteria",
      "Backlog grooming & prioritization",
      "Roadmap & decision making",
      "0→1 product development",
    ],
  },
  {
    id: "delivery",
    title: "Delivery & Operations",
    items: [
      "Agile sprint planning & demos",
      "Cross-functional leadership",
      "Organizational change management",
      "UAT & launch support",
    ],
  },
  {
    id: "customer",
    title: "Customer Experience",
    items: [
      "Customer-facing digital product",
      "Field training & enablement",
      "Stakeholder communications",
      "Issue triage & feedback loops",
    ],
  },
  {
    id: "tools",
    title: "Tools",
    items: [
      "Gainsight PX (platform reporting)",
      "Jira",
      "Zendesk",
      "Confluence",
    ],
  },
  {
    id: "content-communication",
    title: "Content & Communication",
    items: [
      "Release notes",
      "Stakeholder communication",
      "Public speaking",
      "Training materials",
    ],
  },
  {
    id: "technical",
    title: "Technical",
    items: ["HTML/CSS", "Software development", "Entrepreneurial product building"],
  },
  {
    id: "languages",
    title: "Languages",
    items: ["English", "Spanish", "Portuguese"],
  },
];

/** One skill per category for the quick matching game (full lists remain in `skillGroups`). */
const skillMatchItemByGroupId = {
  languages: "Portuguese",
  technical: "Entrepreneurial product building",
};

export const skillMatchPairs = skillGroups.map((group) => ({
  id: group.id,
  category: group.title,
  items: [skillMatchItemByGroupId[group.id] ?? group.items[0]],
}));

export const education = {
  degree: "B.A. in Latin American and Latino Studies",
  school: "University of Pennsylvania",
  graduation: "May 2013",
  extra: "Minor in Music",
  logoUrl: publicAsset("/logos/penn-emblem.png"),
  logoAlt: "University of Pennsylvania shield emblem",
};

export const resumeSectionRail = [
  {
    id: "welcome",
    title: "Welcome",
    summary: "Introduction and Product Owner positioning.",
    meta: "Intro",
  },
  {
    id: "projects",
    title: "Products Built",
    summary: "Ribbons and Immerse—entrepreneurial products built end-to-end.",
    meta: `${keyProjects.length} products`,
  },
  {
    id: "experience",
    title: "Professional Experience",
    summary: "Roles across the SDLC—from enterprise backlog delivery to field enablement.",
    meta: `${experienceItems.length} roles`,
  },
  {
    id: "skills",
    title: "Skills & Credentials",
    summary: "Tools, capabilities, languages, and education.",
    meta: `${skillGroups.length} categories`,
  },
  {
    id: "contact",
    title: "Contact",
    summary: "Reach out through the secure contact form.",
    meta: "Form",
  },
];
